import { Tooltip } from "react-tooltip";
import Image from "next/image";
import CheckIcon from "@/components/Icon/CheckIcon";
import PaPaySetUpIcon from "@/components/Icon/PaPaySetUpIcon";
import PaPayNotSetUpIcon from "@/components/Icon/PaPayNotSetUpIcon";
import ExclamationIcon from "@/components/Icon/ExclamationIcon";
import QuestionIcon from "@/components/Icon/QuestionIcon";
import { useAuthValue } from "@/contexts/contextAuth";
import { setScrollHidden, isMobileDevice } from "@/libs/utils";
import { useEffect, useState } from "react";
import { makeApiImage } from "@/libs/clientConfig";
import Profile from "../Profile";
import { AnimatePresence, motion } from "framer-motion";
import VerificationInfoModal from "../modals/VerificationInfoModal";

type Props = {
  data: any;
};

const PersonalDetailSection = ({ data }: Props) => {
  const { setIsSignup, setSource, setIsListingAction } = useAuthValue();

  const [isMobile, setIsMobile] = useState<boolean>(false);
  const [isInfoModal, setIsInfoModal] = useState<boolean>(false);

  useEffect(() => {
    setIsMobile(isMobileDevice());
  }, []);

  const openVerificationInfo = () => {
    setIsInfoModal(true);
  };

  return (
    <div
      className={`w-full md:w-[820px] px-4 md:px-0 ${
        data?.status == "sold" && "pt-4"
      }`}
    >
      <section className="w-full pt-3">
        <div className="block md:flex justify-between items-center space-y-8 md:space-y-0">
          <div className="flex items-center space-x-4">
            <Profile data={data} imgWidthAndHeight={60}></Profile>
            <div className="space-y-1">
              <div className="text-lg sm:text-2xl font-bold">
                {data?.userId?.userDetails?.nickname != null
                  ? data.userId.userDetails.nickname
                  : data?.userId?.userDetails?.firstName}
                &nbsp;
                {data?.userId?.userDetails?.lastName}.
              </div>
              <div className="text-base sm:text-xl text-neutral-400">
                Joined {data?.userId?.createdAt}
              </div>
            </div>
          </div>
          {data?.status != "sold" && (
            <div className="flex items-center">
              <div className="text-base space-x-2 xs:space-x-4">
                <button
                  className="btn-outline-primary"
                  onClick={() => {
                    setSource("Make Offer");
                    setIsSignup(true, {
                      isListingAction: `${data.slug}?action=createOffer`,
                    });
                    setScrollHidden();
                    setIsListingAction(`${data.slug}?action=createOffer`);
                  }}
                >
                  Make offer
                </button>
                <button
                  className="btn-primary"
                  onClick={() => {
                    setSource("Message Seller");
                    setIsSignup(true, {
                      isListingAction: `${data.slug}?action=message`,
                    });
                    setScrollHidden();
                    setIsListingAction(`${data.slug}?action=message`);
                  }}
                >
                  Message seller
                </button>
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center text-xl sm:text-3xl font-bold mt-8">
          Seller&apos;s Verification&nbsp;
          <QuestionIcon id="verification-question" />
        </div>
        <div className="w-full block sm:flex text-base sm:text-lg pt-4">
          <div className="flex items-center mr-4">
            {data?.userId?.verification?.vouched &&
            data?.userId?.verification?.email &&
            data?.userId?.verification?.phone &&
            data?.userId?.verification?.bank ? (
              <PaPaySetUpIcon />
            ) : data?.userId?.verification?.email ? (
              <CheckIcon />
            ) : (
              <ExclamationIcon />
            )}
            &nbsp;
            <span>Email</span>
          </div>
          <div className="flex items-center mr-4">
            {data?.userId?.verification?.vouched &&
            data?.userId?.verification?.email &&
            data?.userId?.verification?.phone &&
            data?.userId?.verification?.bank ? (
              <PaPaySetUpIcon />
            ) : data?.userId?.verification?.vouched ? (
              <CheckIcon />
            ) : (
              <ExclamationIcon />
            )}
            &nbsp;
            <span>Phone + ID verified</span>
          </div>
          <div className="flex items-center mr-4">
            {data?.userId?.verification?.bank ? (
              <PaPaySetUpIcon />
            ) : (
              <PaPayNotSetUpIcon />
            )}
            &nbsp;
            <span>PrivateAuto Pay connected</span>
          </div>
        </div>
      </section>
      <section className="w-full mt-8">
        <div className="flex items-center text-xl sm:text-3xl font-bold">
          Payment Methods&nbsp;
          <QuestionIcon
            data-tooltip-id="payment-question"
            data-tooltip-content={
              "This is the payment method(s) the seller prefers. We recommend PrivateAuto Pay as it’s the safest option and you can send/receive funds instantly on the spot."
            }
          />
        </div>
        <div className="flex space-x-8 mt-6">
          {data.dealPreferences?.paymentMethod?.privateAutoPay ? (
            <div className="px-4 py-2 border border-[#dee2e6] rounded">
              <Image
                width={46}
                height={17}
                data-tooltip-id="privateauto-pay"
                data-tooltip-content={
                  "PrivateAuto Pay is a instant transasction between a seller and a buyer. Learn more"
                }
                src="/assets/listings/payment-pa-pay.png"
                alt="pa-pay"
              />
            </div>
          ) : (
            ""
          )}
          {data.dealPreferences?.paymentMethod?.cash ? (
            <div className="px-4 py-2 border border-[#dee2e6] rounded">
              <Image
                width={60}
                height={14}
                src="/assets/listings/payment-cash.png"
                alt="cash"
              />
            </div>
          ) : (
            ""
          )}
          {data.dealPreferences?.paymentMethod?.crypto?.btc ? (
            <div className="px-4 py-2 border border-[#dee2e6] rounded">
              <Image
                width={60}
                height={13}
                src="/assets/listings/payment-btc.png"
                alt="bitcoin"
              />
            </div>
          ) : (
            ""
          )}
        </div>
        <Tooltip
          id="privateauto-pay"
          place="top"
          style={{ maxWidth: "250px" }}
        />
        <Tooltip
          id="payment-question"
          place="top"
          style={{ maxWidth: "250px" }}
        />
        <Tooltip
          anchorSelect="#verification-question"
          style={{ maxWidth: "250px" }}
          clickable
          place="top"
        >
          Our verification process ensures a safe environment for everyone. If
          you see an orange warning icon, the seller has not yet completed that
          verification step.
          <button
            style={{ fontWeight: "bold", textDecoration: "underline" }}
            onClick={() => {
              openVerificationInfo();
            }}
          >
            Learn more
          </button>
        </Tooltip>

        <AnimatePresence>
          {isInfoModal && (
            <motion.div
              initial={{ y: -30 }}
              animate={{ y: 0 }}
              exit={{ y: -30, transition: { duration: 0 } }}
              transition={{ duration: 0.2 }}
              className="fixed top-0 left-0 w-full h-screen z-[10000000] inset-0"
            >
              <VerificationInfoModal
                isInfoModal={isInfoModal}
                setIsInfoModal={setIsInfoModal}
              />
            </motion.div>
          )}
        </AnimatePresence>

        <div className="flex py-3 items-center">
          <Image
            src="/assets/dollar-circle-green.svg"
            className="pr-2 w-[25px]"
            width={25}
            height={25}
            alt="money"
          />
          <div className="text-sm font-medium instant-text">
            Instantly send funds with PrivateAuto Pay anytime anywhere.
          </div>
        </div>
        <hr className="hr-thin mt-3 mb-4" />
        {data?.status !== "sold" && (
          <div className="flex justify-between items-center">
            <div className="text-lg font-semibold ready-buy-text">
              Ready to buy this vehicle?
            </div>
            <button
              className="btn-primary w-auto md:w-[125px]"
              onClick={() => {
                setSource("Make Offer");
                setIsSignup(true, {
                  isListingAction: `${data.slug}?action=createOffer`,
                });
                setScrollHidden();
                setIsListingAction(`${data.slug}?action=createOffer`);
              }}
            >
              DealNow
            </button>
          </div>
        )}

        <div className="rounded-[4px] border border-transparent bg-[#F7F9FC] w-full mt-5">
          <div className="m-[20px] flex flex-col items-center justify-center">
            <div className="text-[#212529] text-[24px] font-extrabold leading-[36px] mb-4">
              Why use PrivateAuto
            </div>
            <div className="max-w-[600px] flex  flex-col justify-start">
              <div className="flex align-items-center mb-4 items-center">
                <Image
                  src="/assets/sendMoney.svg"
                  className={`w-[${isMobile ? "55px" : "74px"}]`}
                  width={isMobile ? 55 : 74}
                  height={isMobile ? 55 : 74}
                  alt="money"
                />
                <div className="ml-4">
                  <div className="text-[#000] text-[18px] md:text-[20px] font-medium leading-[16px]">
                    Transfer money safely
                  </div>
                  <div className="text-[#565656] text-[14px] md:text-[16px] font-normal leading-[22px] pt-2">
                    Instantly transfer money between parties with PrivateAuto
                    Pay. No transaction fees.
                  </div>
                </div>
              </div>
              <div className="flex align-items-center mb-4 items-center">
                <Image
                  src="/assets/calendarOffer.svg"
                  className={`w-[${isMobile ? "55px" : "74px"}]`}
                  width={isMobile ? 55 : 74}
                  height={isMobile ? 55 : 74}
                  alt="money"
                />
                <div className="ml-4">
                  <div className="text-[#000]  text-[18px] md:text-[20px] font-medium leading-[16px]">
                    Buying made easy
                  </div>
                  <div className="text-[#565656] text-[14px] md:text-[16px] font-normal leading-[22px] pt-2">
                    Easily schedule test drives, make offers, and e-sign the
                    bill of sale on one app.
                  </div>
                </div>
              </div>
              <div className="flex align-items-center mb-4 items-center">
                <Image
                  src="/assets/chatIcon.svg"
                  className={`w-[${isMobile ? "55px" : "74px"}]`}
                  width={isMobile ? 55 : 74}
                  height={isMobile ? 55 : 74}
                  alt="money"
                />
                <div className="ml-4">
                  <div className="text-[#000]  text-[18px] md:text-[20px] font-medium leading-[16px]">
                    Avoid scammers
                  </div>
                  <div className="text-[#565656] text-[14px] md:text-[16px] font-normal leading-[22px] pt-2">
                    Connect with verified users and chat without sharing your
                    personal info.
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default PersonalDetailSection;
